import { PropsWithChildren } from 'react';

import { SimpleTooltip } from '@bambi/component-library/src/components/Tooltip/SimpleTooltip';

import { useFeature } from 'src/app/FeatureToggle';
import {
  DispatchAssignedDriverRead,
  DispatchAssignmentRead,
  DriverCallRequestRead,
} from 'src/common/external/bambi-api/bambiApi';
import { formatPhoneNumber } from 'src/common/formatPhoneNumber';
import {
  formatFullName,
  getDispatchAssignedDriverDeletedAt,
} from 'src/common/util/formatFullName';
import { useCopyText } from 'src/features/billing/invoices/modals/hooks/useCopyText';

import { MileageBadge } from '../../DispatchCalendarHeader/DispatchCalendarHeaderMenu/MileageBadge';
import { EstimatedRevenueBadge } from '../../DispatchCalendarHeader/EstimatedRevenueBadge';
import AssignmentAttendantsToolTip from './AssignmentAttendantsToolTip';
import { AssignmentHeaderMenu } from './AssignmentHeaderMenu';
import { AssignmentStatusBadge } from './AssignmentStatusBadge';

type AssignmentHeaderProps = {
  assignment: DispatchAssignmentRead;
  numAttendants?: number;
  showCondensed: boolean;
};

export const AssignmentHeader = ({
  assignment,
  showCondensed: isWidthSmall,
}: AssignmentHeaderProps) => {
  const pendingCallRequest = findPendingCallRequest(assignment);
  const { isEnabled: isItineraryEnabled, isLoading: isItineraryLoading } =
    useFeature('itinerary');

  return (
    <HeaderBackground callAttention={!!pendingCallRequest}>
      <div
        className="relative flex w-full flex-row items-center justify-between"
        data-test-id={`assignment-header-${assignment.id}`}
        id={`assignment-header-${assignment.id}`}
      >
        <div
          className={`text-light text-primary relative flex items-center gap-1 truncate pl-[1px] text-left ${
            isWidthSmall && 'text-xs'
          }`}
          title={formatFullName(assignment.driver)}
        >
          {isWidthSmall ? (
            <div className="flex max-w-full flex-col gap-1">
              <DriverPhoneNumberTooltip driver={assignment.driver}>
                <CompactDriverName driver={assignment.driver} />
              </DriverPhoneNumberTooltip>
              <div className="flex items-center gap-1">
                <AssignmentStatusBadge status={assignment.status} />
                {!!assignment.attendants.length && (
                  <AssignmentAttendantsToolTip assignment={assignment} />
                )}
                {assignment.show_pricing ? (
                  <EstimatedRevenueBadge
                    revenue={assignment.estimated_revenue ?? 0}
                  />
                ) : null}
              </div>
            </div>
          ) : (
            <div className="flex max-w-full items-center gap-1">
              <div className="flex-grow">
                <AssignmentStatusBadge status={assignment.status} />
              </div>
              <div className="flex-shrink truncate">
                <DriverPhoneNumberTooltip driver={assignment.driver}>
                  <div>
                    {formatFullName(
                      assignment.driver,
                      getDispatchAssignedDriverDeletedAt
                    )}
                  </div>
                </DriverPhoneNumberTooltip>
              </div>
              {!!assignment.attendants.length && (
                <AssignmentAttendantsToolTip assignment={assignment} />
              )}
              {assignment.show_pricing ? (
                <EstimatedRevenueBadge
                  revenue={assignment.estimated_revenue ?? 0}
                />
              ) : null}
              {!isItineraryLoading && isItineraryEnabled && (
                <>
                  <MileageBadge
                    miles={assignment.loaded_miles ?? 0}
                    type="loaded"
                    simplified
                  />
                  <MileageBadge
                    miles={assignment.unloaded_miles ?? 0}
                    type="unloaded"
                    simplified
                  />
                </>
              )}
            </div>
          )}
        </div>

        <div className="flex">
          <AssignmentHeaderMenu
            assignmentId={assignment.id}
            pendingCallRequest={pendingCallRequest}
          />
        </div>
      </div>
    </HeaderBackground>
  );
};

export const findPendingCallRequest = (
  assignment: DispatchAssignmentRead
): DriverCallRequestRead | undefined => {
  return assignment.driver_call_requests.find(
    (request) => !request.resolved_at && !request.canceled_at
  );
};

interface HeaderBackgroundProps extends PropsWithChildren {
  callAttention: boolean;
}
export const HeaderBackground = ({
  children,
  callAttention,
}: HeaderBackgroundProps) => {
  return (
    <div
      className={`border-l border-r border-t border-gray-200 ${
        callAttention ? 'animate-pulse bg-red-200' : 'bg-gray-50'
      }  py-2 px-1`}
    >
      {children}
    </div>
  );
};

const CompactDriverName = ({
  driver,
}: {
  driver: DispatchAssignedDriverRead;
}) => {
  return (
    <DriverPhoneNumberTooltip driver={driver}>
      <div>
        <div className="truncate">{driver.first_name}</div>
        <div className="truncate">{driver.last_name}</div>
        {driver.deleted_at && <div>[deleted]</div>}
      </div>
    </DriverPhoneNumberTooltip>
  );
};

function DriverPhoneNumberTooltip({
  driver,
  children,
}: {
  driver: DispatchAssignedDriverRead;
} & PropsWithChildren) {
  const copyText = useCopyText();

  if (!driver.phone_number) {
    return <>{children}</>;
  }

  const formattedPhoneNumber = formatPhoneNumber(driver.phone_number);

  return (
    <SimpleTooltip
      trigger={children}
      testId="driver-phone-number-tooltip"
      content={
        <div onClick={() => copyText(formattedPhoneNumber)}>
          {formattedPhoneNumber}
        </div>
      }
    />
  );
}
